import { useEffect, useRef, useState } from 'react';
import { Link2, X } from 'lucide-react';
import { deviceDisplayName, deviceNumSchema, type BindResult, type Device } from '@mytag/shared';
import { useBindDevice, useUnbindDevice } from '@/hooks/queries';
import { Button, Field, Input } from '@/components/ui/primitives';

function maskedDeviceNum(deviceNum: string): string {
  return deviceNum.length > 8 ? `${deviceNum.slice(0, 4)}…${deviceNum.slice(-4)}` : deviceNum;
}

/** Message court et compréhensible ; jamais de code, requestId ou diagnostic technique affiché ici. */
function shortErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Une erreur est survenue. Réessayez.';
}

/**
 * Coquille de modal partagée : déplace le focus dans le dialogue à
 * l'ouverture, ferme sur Échap, et restaure le focus sur l'élément
 * déclencheur à la fermeture (accessibilité clavier).
 */
function ModalShell({
  titleId,
  onDismiss,
  children,
}: {
  titleId: string;
  onDismiss: () => void;
  children: React.ReactNode;
}): JSX.Element {
  const sectionRef = useRef<HTMLElement>(null);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const section = sectionRef.current;
    const focusable = section?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (focusable ?? section)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismissRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
    // Une seule fois par montage réel : la ref porte toujours le dernier onDismiss.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-foreground/40 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <section
        ref={sectionRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-5 shadow-xl"
      >
        {children}
      </section>
    </div>
  );
}

/**
 * Association réelle : formulaire -> deuxième confirmation (retype des 4
 * derniers caractères) -> appel bind -> résultat affiché de façon minimale.
 * N'accepte jamais le CID, le token, le userId ou la clé 3DES : ces valeurs
 * viennent exclusivement de la session backend.
 */
export function BindDeviceDialog({
  existingDeviceNums,
  initialDeviceNum = '',
  onClose,
}: {
  existingDeviceNums: string[];
  initialDeviceNum?: string;
  onClose: () => void;
}): JSX.Element {
  const bind = useBindDevice();
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [deviceNum, setDeviceNum] = useState(initialDeviceNum);
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [result, setResult] = useState<BindResult | null>(null);
  const submitting = useRef(false);

  const trimmed = deviceNum.trim();
  const last4 = trimmed.slice(-4).toLowerCase();

  const proceedToConfirm = () => {
    if (!trimmed) {
      setFormError('Le deviceNum est requis.');
      return;
    }
    const parsed = deviceNumSchema.safeParse(trimmed);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'deviceNum invalide.');
      return;
    }
    if (existingDeviceNums.includes(trimmed)) {
      setFormError('Cet appareil figure déjà dans la liste fournisseur du compte connecté.');
      return;
    }
    setFormError(null);
    setConfirmInput('');
    setStep('confirm');
  };

  const confirmDisabled =
    bind.isPending || confirmInput.trim().toLowerCase() !== last4 || last4.length === 0;

  const submit = () => {
    if (submitting.current || confirmDisabled) return;
    submitting.current = true;
    bind.mutate(
      { deviceNum: trimmed, name: name.trim() || undefined, confirmReal: true },
      {
        onSuccess: (data) => setResult(data),
        onSettled: () => {
          submitting.current = false;
        },
      },
    );
  };

  if (result) {
    return (
      <ModalShell titleId="bind-success-title" onDismiss={onClose}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 id="bind-success-title" className="text-base font-semibold">
            Association réussie
          </h2>
          <Button type="button" size="icon" variant="ghost" aria-label="Fermer" icon={<X className="h-4 w-4" />} onClick={onClose} />
        </div>
        <p className="text-sm text-foreground">L’appareil a été associé avec succès.</p>
        <div className="mt-5 flex justify-end">
          <Button type="button" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell titleId="bind-device-title" onDismiss={() => !bind.isPending && onClose()}>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h2 id="bind-device-title" className="text-base font-semibold">
            Associer un SmartTag
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {step === 'form'
              ? 'Le numéro doit être une référence fournisseur (deviceNum) Vernal valide.'
              : 'Opération réelle Vernal : vérifiez le deviceNum avant de confirmer.'}
          </p>
        </div>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Fermer"
          icon={<X className="h-4 w-4" />}
          disabled={bind.isPending}
          onClick={onClose}
        />
      </div>

      {step === 'form' ? (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            proceedToConfirm();
          }}
        >
          <Field label="Numéro de device (deviceNum)" htmlFor="bind-device-num">
            <Input
              id="bind-device-num"
              value={deviceNum}
              onChange={(event) => setDeviceNum(event.target.value)}
              placeholder="1756814431069678"
              className="font-mono"
              autoFocus
              required
            />
          </Field>
          <Field label="Nom (optionnel)" htmlFor="bind-device-name">
            <Input id="bind-device-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Tag Valise" />
          </Field>
          {formError ? <p className="text-sm text-danger">{formError}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" icon={<Link2 className="h-4 w-4" />} disabled={!trimmed}>
              Vérifier et associer
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-warning">
            <p className="font-medium">Opération réelle Vernal</p>
            <p className="mt-1 text-xs text-foreground/80">
              Cette opération associera réellement le SmartTag au compte Vernal actuellement
              connecté. Vérifiez que le deviceNum correspond au bon appareil.
            </p>
          </div>
          <p className="font-mono text-xs text-muted-foreground">deviceNum : {maskedDeviceNum(trimmed)}</p>
          <Field
            label={`Retapez les 4 derniers caractères du deviceNum (${trimmed.slice(-4)}) pour confirmer`}
            htmlFor="bind-confirm-last4"
          >
            <Input
              id="bind-confirm-last4"
              value={confirmInput}
              onChange={(event) => setConfirmInput(event.target.value)}
              className="font-mono"
              autoFocus
              maxLength={4}
            />
          </Field>
          {bind.isError ? <p className="text-sm text-danger">{shortErrorMessage(bind.error)}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" disabled={bind.isPending} onClick={() => setStep('form')}>
              Retour
            </Button>
            <Button
              type="button"
              loading={bind.isPending}
              disabled={confirmDisabled}
              icon={<Link2 className="h-4 w-4" />}
              onClick={submit}
            >
              Vérifier et associer
            </Button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

/**
 * Dissociation réelle : distincte de l'archivage local. Le clic sur
 * « Dissocier » constitue la confirmation explicite ; aucune case à cocher
 * ni retype n'est requis ici. Ne change pas le payload Vernal ni les
 * protections backend (ALLOW_REAL_BIND_UNBIND, verrou anti-concurrence,
 * vérification post-opération) : uniquement l'affichage est simplifié.
 */
export function UnbindDeviceDialog({
  device,
  onClose,
}: {
  device: Device;
  onClose: () => void;
}): JSX.Element {
  const unbind = useUnbindDevice();
  const [done, setDone] = useState(false);
  const submitting = useRef(false);

  const submit = () => {
    if (submitting.current || unbind.isPending) return;
    submitting.current = true;
    unbind.mutate(
      { deviceNum: device.deviceNum, confirmReal: true },
      {
        onSuccess: () => setDone(true),
        onSettled: () => {
          submitting.current = false;
        },
      },
    );
  };

  if (done) {
    return (
      <ModalShell titleId="unbind-success-title" onDismiss={onClose}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 id="unbind-success-title" className="text-base font-semibold">
            Dissociation réussie
          </h2>
          <Button type="button" size="icon" variant="ghost" aria-label="Fermer" icon={<X className="h-4 w-4" />} onClick={onClose} />
        </div>
        <p className="text-sm text-foreground">L’appareil a été dissocié avec succès.</p>
        <div className="mt-5 flex justify-end">
          <Button type="button" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell titleId="unbind-device-title" onDismiss={() => !unbind.isPending && onClose()}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <h2 id="unbind-device-title" className="text-base font-semibold">
          Dissocier l’appareil
        </h2>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Fermer"
          icon={<X className="h-4 w-4" />}
          disabled={unbind.isPending}
          onClick={onClose}
        />
      </div>
      <p className="text-sm font-medium">{deviceDisplayName(device)}</p>
      <p className="mt-2 text-sm text-muted-foreground">
        Cet appareil sera dissocié de votre compte. Voulez-vous continuer ?
      </p>
      {unbind.isError ? <p className="mt-3 text-sm text-danger">{shortErrorMessage(unbind.error)}</p> : null}
      <div className="mt-5 flex justify-end gap-2">
        <Button type="button" variant="ghost" disabled={unbind.isPending} onClick={onClose}>
          Annuler
        </Button>
        <Button type="button" variant="danger" loading={unbind.isPending} disabled={unbind.isPending} onClick={submit}>
          {unbind.isPending ? 'Dissociation…' : 'Dissocier'}
        </Button>
      </div>
    </ModalShell>
  );
}
